import { ProductStatus } from '@qcdoc/mock-runtime/contracts/enums.ts';
import apiClient from '../../api/common/apiClient.ts';
import { Endpoints } from '@qcdoc/mock-runtime/contracts/endpoints.ts';
import type { ApiResponse, PagedRequest } from '../../types/common/common.ts';
import type {
  ProductListItem,
  ProductDetail,
  CreateProductDto,
  UpdateProductDto,
  ProductFormula,
  AddFormulaDto,
  AddSpecDto,
  AddSupplierDto,
  ApprovedSupplier,
  ProductApprovalResult,
  ProductVendorSections,
  SupersedeChainNode,
  HasExistingPendingProdResponse,
  HasBeenSupercededResponse,
  ProdSpecAuditEntry,
  ProductAuditEntry,
} from '../../types/products/product.types.ts';

/** Map SPA sort field (antd dataIndex) → backend SortBy value */
function buildSortBy(params: PagedRequest): string | undefined {
  if (!params.sortField) return undefined;
  const fieldMap: Record<string, string> = {
    name: 'NAME',
    formerName: 'FORMERNAME',
    statusName: 'STATUSNAME',
    shelfLifeYears: 'SHELFLIFE',
    createdDate: 'CREATEDDATE',
    preCode: 'PRECODE',
    productSAPNumber: 'SAPNUMBER',
  };
  const key = fieldMap[params.sortField] ?? params.sortField.toUpperCase();
  const dir = params.sortDirection === 'desc' ? 'DESC' : 'ASC';
  return `${key} ${dir}`;
}

/** Map SPA filter keys → ProductPagingDTO / ProductFilterDTO properties */
function buildFilterBody(params: PagedRequest) {
  const f = params.filters ?? {};
  return {
    ProdName: f.productName ? `%${f.productName}%` : undefined,
    Statuses: Array.isArray(f.status) && f.status.length > 0
      ? f.status.join(',')
      : (f.status || undefined),
    PslId: f.pslId || undefined,
    ProductSAPNumber: f.sapNumber ? `%${f.sapNumber}%` : undefined,
    ProdDescription: f.description ? `%${f.description}%` : undefined,
    ProdFormerName: f.formerName ? `%${f.formerName}%` : undefined,
    ProdTypeId: f.productTypeId || undefined,
    PreCode: f.productRm || undefined,
    ShelfLife: f.shelfLife ? parseFloat(f.shelfLife) : undefined,
    Notes: f.productNotes ? `%${f.productNotes}%` : undefined,
    ApprovedSupplier: f.vendorId || undefined,
    VenProdName: f.vendorProductName ? `%${f.vendorProductName}%` : undefined,
    ProdComponent: f.formula ? `%${f.formula}%` : undefined,
    FormulaKey: f.formulaNotes ? `%${f.formulaNotes}%` : undefined,
    MDescription: f.mfgDescription ? `%${f.mfgDescription}%` : undefined,
    STPNumber: f.stpNumber ? `%${f.stpNumber}%` : undefined,
    STPName: f.stpName ? `%${f.stpName}%` : undefined,
    SpecNotes: f.specNotes ? `%${f.specNotes}%` : undefined,
    TypicalProp: f.typicalProperties ? `%${f.typicalProperties}%` : undefined,
    PkgNotes: f.packagingNotes ? `%${f.packagingNotes}%` : undefined,
    History: f.history ? `%${f.history}%` : undefined,
  };
}

/** Lightweight product shape returned by GET /api/Product/activeProd */
interface ActiveProductItem {
  oid: string;
  name: string;
  productSAPNumber?: string;
  description?: string;
}

export const productApi = {
  getSpecs: (productId: string, signal?: AbortSignal) =>
    apiClient.post<ProdSpecResponse[]>(Endpoints.prodSpec.getByFilters, { ProdId: productId }, { signal })
      .then((response) => ({ data: structuredClone(Array.isArray(response.data) ? response.data : []) })),
  updateSpec: (payload: UpdateSpecPayload, currentUser: string, signal?: AbortSignal) =>
    apiClient.put(Endpoints.prodSpec.update, {
      SpecId: payload.specId, SortNum: payload.sortNum, Min: payload.min, Max: payload.max,
      Visual: payload.visual, VisualQ: payload.visualQ, SpecNotes: payload.specNotes, LastUpdatedBy: currentUser,
    }, { signal }).then(() => undefined),
  deleteSpec: (specId: string, signal?: AbortSignal) =>
    apiClient.delete(Endpoints.prodSpec.delete(specId), { signal }).then(() => undefined),
  /**
   * POST body: ProductPagingDTO → { items: ProductListItem[], totalCount: number }
   * Single request returns both paged data and total count.
   */
  getList: (params: PagedRequest, signal?: AbortSignal) =>
    apiClient.post<{ items: ProductListItem[]; totalCount: number }>(Endpoints.products.list, {
      ...buildFilterBody(params),
      StartIndex: ((params.page ?? 1) - 1) * (params.pageSize ?? 15),
      MaximumRows: params.pageSize ?? 15,
      SortBy: buildSortBy(params),
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })).then((response) => ({ data: structuredClone(response.data) })),

  /** GET — all active products (for dropdowns/lookups) */
  getActiveProducts: (signal?: AbortSignal) =>
    apiClient.get<ActiveProductItem[]>(Endpoints.products.active, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** GET ?prodId={Guid} */
  getById: (id: string, signal?: AbortSignal) =>
    apiClient.get<ProductDetail | ProductDetail[]>(Endpoints.products.byId, { params: { prodId: id }, signal })
      .then((response) => ({ data: structuredClone(Array.isArray(response.data) ? response.data[0] : response.data) })),

  /** POST body: AddProductDTO — maps camelCase DTO to PascalCase for backend binding */
  create: (dto: CreateProductDto, signal?: AbortSignal) =>
    apiClient.post<string>(Endpoints.products.create, {
      ProductSAPNumber: dto.productSAPNumber,
      Name: dto.name,
      Description: dto.description,
      FormerName: dto.formerName,
      IsCOQDisplay: dto.isCOQDisplay,
      PreCode: dto.preCode,
      ProdRMId: dto.prodRMId || undefined,
      RegistrationTypeId: dto.registrationTypeId || undefined,
      ProductTypeId: dto.productTypeId || undefined,
      PslID: dto.pslID,
      ShelfLifeYears: dto.shelfLifeYears,
      IssueDate: dto.issueDate,
      Supercedes: dto.supercedes,
      SupercedeId: dto.supercedeId || undefined,
      CAS_No: dto.casNo,
      TradeMarkOwner: dto.tradeMarkOwner,
      Notes: dto.notes,
      SupplierNotes: dto.supplierNotes,
      FormulaNotes: dto.formulaNotes,
      MDescription: dto.mDescription,
      SpecNotes: dto.specNotes,
      TypicalProperties: dto.typicalProperties,
      PackagingNotes: dto.packagingNotes,
      ProdPackage: dto.prodPackage,
      PkgType: dto.pkgType,
      ProdImageURL: dto.prodImageURL,
      StoragePrecaution: dto.storagePrecaution,
      USADot: dto.usaDot,
      HazardClass: dto.hazardClass,
      HazardSubstance: dto.hazardSubstance,
      HTSNumber: dto.htsNumber,
      CodePageNum: dto.codePageNum,
      ReportableQty: dto.reportableQty,
      ClassNum: dto.classNum,
      PkgingGroup: dto.pkgingGroup,
      MotorFrightNum: dto.motorFrightNum,
      DOT_Label: dto.dotLabel,
      DOT_PlaCard: dto.dotPlaCard,
      DOT_IDNum: dto.dotIdNum,
      DOT_DOTNotes: dto.dotDotNotes,
      DOT_ShippingName: dto.dotShippingName,
      IMO_Label: dto.imoLabel,
      IMO_PlaCard: dto.imoPlaCard,
      IMO_IDNum: dto.imoIdNum,
      IMO_Notes: dto.imoNotes,
      IMO_ShippingName: dto.imoShippingName,
      ReqACountries: dto.reqACountries,
      EECCountries: dto.eecCountries,
      History: dto.history,
      Status: dto.status,
      CreatedBy: dto.createdBy,
      LastUpdatedBy: dto.lastUpdatedBy,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** GET ?prodName={name} — returns list of Guids if name exists */
  getProdId: (prodName: string, signal?: AbortSignal) =>
    apiClient.get<string[]>(Endpoints.products.getProdId, { ...({ params: { prodName } }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT body: UpdateNotesDTO — NOTE: backend has separate update endpoints per field group */
  update: (_id: string, dto: UpdateProductDto, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateNotes, dto, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /**
   * PUT body: UpdateProductDTO — single atomic update for ALL product fields.
   * Mirrors the legacy updateAll() pattern: one SELECT + one UPDATE.
   * Backend only writes non-null fields, so omit fields you don't want to change.
   */
  updateBasicInfo: (dto: {
    // Identity
    oid: string;
    // Section I – Basic Info
    productSAPNumber?: string; name?: string; description?: string;
    formerName?: string; isCOQDisplay?: string; preCode?: string;
    prodRMId?: string; registrationTypeId?: string; productTypeId?: string;
    pslID?: string; shelfLifeYears?: number; issueDate?: string;
    supercedes?: string; casNo?: string; tradeMarkOwner?: string;
    notes?: string;
    // Section II – Formula
    formulaNotes?: string;
    // Section III – Mfg Description
    mDescription?: string;
    // Spec Notes
    specNotes?: string;
    // Section IV – Typical Properties
    typicalProperties?: string;
    // Section V – Packaging
    packagingNotes?: string; prodPackage?: string; pkgType?: string;
    prodImageURL?: string;
    // Section VI – Storage
    storagePrecaution?: string;
    // Section VII – Transport Regulations
    usaDot?: string; hazardClass?: string; hazardSubstance?: string;
    htsNumber?: string; codePageNum?: string; reportableQty?: string;
    classNum?: string; pkgingGroup?: string; motorFrightNum?: string;
    dot_Label?: string; dot_PlaCard?: string; dot_IDNum?: string;
    dot_DOTNotes?: string; dot_ShippingName?: string;
    imo_Label?: string; imo_PlaCard?: string; imo_IDNum?: string;
    imo_Notes?: string; imo_ShippingName?: string;
    reqACountries?: string; eecCountries?: string;
    // Section IX – History
    history?: string;
    // Supplier Notes
    supplierNotes?: string;
    // Audit
    lastUpdatedBy: string;
  }, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateBasicInfo, {
      Oid: dto.oid,
      // Section I
      ProductSAPNumber: dto.productSAPNumber,
      Name: dto.name,
      Description: dto.description,
      FormerName: dto.formerName,
      IsCOQDisplay: dto.isCOQDisplay,
      PreCode: dto.preCode,
      ProdRMId: dto.prodRMId || undefined,
      RegistrationTypeId: dto.registrationTypeId || undefined,
      ProductTypeId: dto.productTypeId || undefined,
      PslID: dto.pslID,
      ShelfLifeYears: dto.shelfLifeYears,
      IssueDate: dto.issueDate,
      Supercedes: dto.supercedes,
      CAS_No: dto.casNo,
      TradeMarkOwner: dto.tradeMarkOwner,
      Notes: dto.notes,
      // Section II
      FormulaNotes: dto.formulaNotes,
      // Section III
      MDescription: dto.mDescription,
      // Spec Notes
      SpecNotes: dto.specNotes,
      // Section IV
      TypicalProperties: dto.typicalProperties,
      // Section V
      PackagingNotes: dto.packagingNotes,
      ProdPackage: dto.prodPackage,
      PkgType: dto.pkgType,
      ProdImageURL: dto.prodImageURL,
      // Section VI
      StoragePrecaution: dto.storagePrecaution,
      // Section VII
      USADot: dto.usaDot,
      HazardClass: dto.hazardClass,
      HazardSubstance: dto.hazardSubstance,
      HTSNumber: dto.htsNumber,
      CodePageNum: dto.codePageNum,
      ReportableQty: dto.reportableQty,
      ClassNum: dto.classNum,
      PkgingGroup: dto.pkgingGroup,
      MotorFrightNum: dto.motorFrightNum,
      DOT_Label: dto.dot_Label,
      DOT_PlaCard: dto.dot_PlaCard,
      DOT_IDNum: dto.dot_IDNum,
      DOT_DOTNotes: dto.dot_DOTNotes,
      DOT_ShippingName: dto.dot_ShippingName,
      IMO_Label: dto.imo_Label,
      IMO_PlaCard: dto.imo_PlaCard,
      IMO_IDNum: dto.imo_IDNum,
      IMO_Notes: dto.imo_Notes,
      IMO_ShippingName: dto.imo_ShippingName,
      ReqACountries: dto.reqACountries,
      EECCountries: dto.eecCountries,
      // Section IX
      History: dto.history,
      // Supplier Notes
      SupplierNotes: dto.supplierNotes,
      // Audit
      LastUpdatedBy: dto.lastUpdatedBy,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  // ── Per-section update methods (mirrors legacy updateAll) ──

  /** PUT body: UpdateNotesDTO { ProdId, Notes, CurrentUser } */
  updateNotes: (prodId: string, notes: string, currentUser: string, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateNotes, {
      ProdId: prodId, Notes: notes, CurrentUser: currentUser,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT body: UpdateNotesDTO { ProdId, Notes, CurrentUser } — formula notes reuses same DTO */
  updateFormulaNotes: (prodId: string, notes: string, currentUser: string, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateFormulaNotes, {
      ProdId: prodId, Notes: notes, CurrentUser: currentUser,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT ?prodId=&MDescription=&currentUser= */
  updateMDescription: (prodId: string, mDescription: string, currentUser: string, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateMDescription, null, { ...({
      params: { prodId, MDescription: mDescription, currentUser },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT ?prodId=&typicalProperties=&currentUser= */
  updateTypicalProp: (prodId: string, typicalProperties: string, currentUser: string, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateTypicalProp, null, { ...({
      params: { prodId, typicalProperties, currentUser },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT body: UpdatePackagingDTO */
  updatePackaging: (dto: {
    prodId: string; packagingNotes?: string; prodPackage?: string;
    reqACountries?: string; pkgType?: string; eecCountries?: string;
    usaDot?: string; prodImageURL?: string; currentUser: string;
  }, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updatePackaging, {
      ProdId: dto.prodId,
      PackagingNotes: dto.packagingNotes,
      ProdPackage: dto.prodPackage,
      ReqACountries: dto.reqACountries,
      PkgType: dto.pkgType,
      EECCountries: dto.eecCountries,
      USADot: dto.usaDot,
      ProdImageURL: dto.prodImageURL,
      CurrentUser: dto.currentUser,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT ?prodId=&storagePrecaution=&currentUser= */
  updateStoragePrecaution: (prodId: string, storagePrecaution: string, currentUser: string, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateStoragePrecaution, null, { ...({
      params: { prodId, storagePrecaution, currentUser },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT body: UpdateTranRegulationsDTO */
  updateTranRegulations: (dto: {
    prodId: string; hazardClass?: string; hazardSubstance?: string;
    htsNumber?: string; codePageNum?: string; reportableQty?: string;
    classNum?: string; pkgingGroup?: string; motorFrightNum?: string;
    dot_Label?: string; dot_PlaCard?: string; dot_IDNum?: string;
    dot_DOTNotes?: string; dot_ShippingName?: string;
    imo_Label?: string; imo_PlaCard?: string; imo_IDNum?: string;
    imo_Notes?: string; imo_ShippingName?: string; currentUser: string;
  }, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateTranRegulations, {
      ProdId: dto.prodId,
      HazardClass: dto.hazardClass,
      HazardSubstance: dto.hazardSubstance,
      HTSNumber: dto.htsNumber,
      CodePageNum: dto.codePageNum,
      ReportableQty: dto.reportableQty,
      ClassNum: dto.classNum,
      PkgingGroup: dto.pkgingGroup,
      MotorFrightNum: dto.motorFrightNum,
      DOT_Label: dto.dot_Label,
      DOT_PlaCard: dto.dot_PlaCard,
      DOT_IDNum: dto.dot_IDNum,
      DOT_DOTNotes: dto.dot_DOTNotes,
      DOT_ShippingName: dto.dot_ShippingName,
      IMO_Label: dto.imo_Label,
      IMO_PlaCard: dto.imo_PlaCard,
      IMO_IDNum: dto.imo_IDNum,
      IMO_Notes: dto.imo_Notes,
      IMO_ShippingName: dto.imo_ShippingName,
      CurrentUser: dto.currentUser,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT body: { ProdId, Notes (history text), CurrentUser } — appends like Notes */
  updateHistory: (prodId: string, history: string, currentUser: string, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.updateHistory, {
      ProdId: prodId, Notes: history, CurrentUser: currentUser,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT ?currentUser={string}&prodId={Guid} — approves product (status → Approved) */
  approve: (id: string, currentUser?: string, signal?: AbortSignal) =>
    apiClient.put<ProductApprovalResult>(Endpoints.products.approve, null, { ...({
      params: { prodId: id, currentUser: currentUser ?? '' },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** DELETE ?prodId={Guid} */
  delete: (id: string, signal?: AbortSignal) =>
    apiClient.delete<ApiResponse>(Endpoints.products.delete, { ...({ params: { prodId: id } }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT ?currentUser=&prodId= */
  duplicate: (id: string, currentUser?: string, signal?: AbortSignal) =>
    apiClient.put<ProductDetail>(Endpoints.products.duplicate, null, { ...({
      params: { prodId: id, currentUser: currentUser ?? '' },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT ?currentUser=&prodId= */
  deactivate: (id: string, currentUser?: string, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.deactivate, null, { ...({
      params: { prodId: id, currentUser: currentUser ?? '' },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** PUT ?currentUser=&prodId= */
  reactivate: (id: string, currentUser?: string, signal?: AbortSignal) =>
    apiClient.put<ApiResponse>(Endpoints.products.reactivate, null, { ...({
      params: { prodId: id, currentUser: currentUser ?? '' },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  // Formulas — FormulaController
  /** GET ?ProdId={Guid} */
  getFormulas: (id: string, signal?: AbortSignal) =>
    apiClient.get<ProductFormula[]>(Endpoints.formulas.byProductId, { ...({ params: { ProdId: id } }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** POST body: FormulaDTO { ProductId, SequenceNo, RMId, Weight, CreatedBy } */
  addFormula: (dto: AddFormulaDto, signal?: AbortSignal) =>
    apiClient.post<ProductFormula>(Endpoints.formulas.add, {
      ProductId: dto.productId,
      SequenceNo: dto.sequenceNo,
      RMId: dto.rmId,
      Weight: dto.weight,
      CreatedBy: dto.createdBy,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** DELETE api/Formula/{Oid} */
  deleteFormula: (formulaOid: string, signal?: AbortSignal) =>
    apiClient.delete(Endpoints.formulas.delete(formulaOid), { signal }).then((response) => ({ data: structuredClone(response.data) })),

  // Specifications — ProdSpecController
  /** POST body: SpecInsertDTO */
  addSpec: (dto: AddSpecDto, signal?: AbortSignal) =>
    apiClient.post(Endpoints.prodSpec.create, {
      ProductId: dto.productId,
      SpecTypeId: dto.specTypeId,
      STPId: dto.stpId,
      Max: dto.max,
      Min: dto.min,
      Visual: dto.visual ?? false,
      VisualQ: dto.visualQ,
      SpecNotes: dto.specNotes,
      SortNum: dto.sortNum ?? 0,
      IsSaleSpec: dto.isSaleSpec ?? false,
      IsPerfSpec: dto.isPerfSpec ?? false,
      IsActive: dto.isActive ?? true,
      CreatedBy: dto.createdBy,
      LastUpdatedBy: dto.lastUpdatedBy,
      PSLId: dto.pslId,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** GET /api/ProdSpec/audit/{specId} — returns audit trail for a specific ProdSpec */
  getSpecAudit: (specId: string, signal?: AbortSignal) =>
    apiClient.get<ProdSpecAuditEntry[]>(Endpoints.prodSpec.audit(specId), { signal }).then((response) => ({ data: structuredClone(response.data) })),

  // Approved Suppliers — ApprovedProdSupplierController
  /** POST body: AddApprovedProdSupplierDTO */
  addSupplier: (dto: AddSupplierDto, signal?: AbortSignal) =>
    apiClient.post(Endpoints.approvedSuppliers.create, {
      ProductId: dto.productId,
      TradeName: dto.tradeName,
      VendorId: dto.vendorId,
      CreatedBy: dto.createdBy,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  // Approvals — ProdApprovalController
  /** GET ?ProdId={Guid} */
  getApprovals: (id: string, signal?: AbortSignal) =>
    apiClient.get<ProdApprovalResponse[]>(Endpoints.prodApprovals.byProdId, { ...({ params: { ProdId: id } }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** POST body: ProdApprovalDTO { ProdId, Name, Department, Date, Notes, CreatedBy } */
  addApproval: (dto: {
    prodId: string;
    name: string;
    department: string;
    date: string;
    notes?: string;
    createdBy: string;
  }, signal?: AbortSignal) =>
    apiClient.post<ApiResponse>(Endpoints.prodApprovals.create, {
      ProdId: dto.prodId,
      Name: dto.name,
      Department: dto.department,
      Date: dto.date,
      Notes: dto.notes,
      CreatedBy: dto.createdBy,
    }, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** DELETE ?Oid={Guid} */
  deleteApproval: (approvalOid: string, signal?: AbortSignal) =>
    apiClient.delete<ApiResponse>(Endpoints.prodApprovals.delete, { ...({
      params: { Oid: approvalOid },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  // PSL Assignments — TODO: No PSL assignment endpoints in backend
  getPsls: (_id: string, _signal?: AbortSignal) => {
    return Promise.reject(new Error('Not implemented in backend'));
  },

  addPsl: (_id: string, _pslId: number, _signal?: AbortSignal) => {
    return Promise.reject(new Error('Not implemented in backend'));
  },

  deletePsl: (_id: string, _assignmentId: number, _signal?: AbortSignal) => {
    return Promise.reject(new Error('Not implemented in backend'));
  },

  // Approved Suppliers
  /** Combine non-VQCA and VQCA-approved suppliers, matching the legacy report. */
  getApprovedSuppliers: async (params: PagedRequest, signal?: AbortSignal) => {
    const config = { ...({ params: params.filters }), signal };
    const [approved, approvedVqca] = await Promise.all([
      apiClient.get<ApprovedSupplier[]>(Endpoints.products.getApprovedSuppliers, config),
      apiClient.get<ApprovedSupplier[]>(Endpoints.products.getApprovedVQCASuppliers, config),
    ]);
    const suppliers = [...approved.data, ...approvedVqca.data];
    return { data: structuredClone([...new Map(suppliers.map((supplier) => [supplier.oid, supplier])).values()]) };
  },

  /**
   * GET ?ProductId=&ProdcutName= — approved VQCA suppliers for one product.
   *
   * Legacy `ctlApprovedSuppliers` binds on the product **name** only (it calls
   * `sp_selApprovedVQCASuppliers` with `@ProductId = NULL`), because a product's
   * supersede chain creates a new `Product.Oid` while existing VQCAs stay attached
   * to the previous row. Filtering by Oid alone therefore returns nothing for any
   * superseded product — so pass the name when it is known.
   */
  getApprovedSuppliersByProduct: (productId: string, productName?: string, signal?: AbortSignal) =>
    apiClient.get<ApprovedSupplier[]>(Endpoints.products.getApprovedSuppliers, { ...({
      params: productName ? { ProdcutName: productName } : { ProductId: productId },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** Legacy parity: VQCA vendors bind by product name (across versions); Non-VQCA vendors bind by this product's own Oid. */
  getProductVendorSections: async (productId: string, productName?: string, signal?: AbortSignal): Promise<{ data: ProductVendorSections }> => {
    const vqcaParams = productName ? { ProdcutName: productName } : { ProductId: productId };
    const [nonVqca, vqca] = await Promise.all([
      apiClient.get<ApprovedSupplier[]>(Endpoints.products.getApprovedSuppliers, { params: { ProductId: productId }, signal }),
      apiClient.get<ApprovedSupplier[]>(Endpoints.products.getApprovedVQCASuppliers, { params: vqcaParams, signal }),
    ]);
    return { data: { nonVqcaVendors: structuredClone(nonVqca.data), vqcaVendors: structuredClone(vqca.data) } };
  },

  /** GET COA lot count per vendor for a product (Section XII). */
  getCOALotCount: (productId: string, signal?: AbortSignal) =>
    apiClient.get<{ vendor: string; count: number }[]>(Endpoints.products.getCOALotCount, { ...({
      params: { ProductId: productId },
    }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** GET the product change-audit trail (Audit History). */
  getProductAudit: (productId: string, signal?: AbortSignal) =>
    apiClient.get<ProductAuditEntry[]>(Endpoints.products.audit(productId), { signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** POST body: AddApprovedProdSupplierDTO — ApprovedProdSupplierController */
  createApprovedSupplier: (dto: Partial<ApprovedSupplier>, signal?: AbortSignal) =>
    apiClient.post<ApiResponse>(Endpoints.approvedSuppliers.create, dto, { signal }).then((response) => ({ data: structuredClone(response.data) })),

  deleteApprovedSupplier: (_id: string, _signal?: AbortSignal) => {
    // TODO: No delete endpoint in ApprovedProdSupplierController
    return Promise.reject(new Error('Not implemented in backend'));
  },

  // ── Supersede ──

  /** GET ?prodId={Guid} — chain downward (older products this one replaced) */
  getSupercedingProducts: (prodId: string, signal?: AbortSignal) =>
    apiClient.get<SupersedeChainNode[]>(Endpoints.products.supercedingProdById, { ...({ params: { prodId } }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** GET ?prodId={Guid} — chain upward (newer products that replaced this one) */
  getSupercingProducts: (prodId: string, signal?: AbortSignal) =>
    apiClient.get<SupersedeChainNode[]>(Endpoints.products.supercingProdById, { ...({ params: { prodId } }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** GET ?prodId={Guid} — check if a pending version of this product exists */
  hasExistingPendingProd: (prodId: string, signal?: AbortSignal) =>
    apiClient.get<HasExistingPendingProdResponse>(Endpoints.products.pendingProd, { ...({ params: { prodId } }), signal }).then((response) => ({ data: structuredClone(response.data) })),

  /** GET ?prodId={Guid} — check if this product has been superceded */
  hasBeenSuperceded: (prodId: string, signal?: AbortSignal) =>
    apiClient.get<HasBeenSupercededResponse>(Endpoints.products.hasBeenSuperceded, { ...({ params: { prodId } }), signal }).then((response) => ({ data: structuredClone(response.data) })),
};

export interface ProdSpecResponse {
  specId: string;
  productId?: string;
  specTypeId?: string;
  specTypeName?: string;
  stpId?: string;
  stpName?: string;
  stpNumber?: string;
  stpurl?: string;
  max?: number;
  min?: number;
  visual?: boolean;
  visualQ?: string;
  specNotes?: string;
  sortNum?: number;
  isSaleSpec?: boolean;
  isPerfSpec?: boolean;
  isActive?: boolean;
  pslId?: string;
  prodName?: string;
  prodDesc?: string;
  prodShelfYear?: number;
  productSAPNumber?: string;
  coAid?: string;
  vqcaId?: string;
  numVal?: number;
}
export interface UpdateSpecPayload {
  specId: string;
  sortNum?: number;
  min?: number;
  max?: number;
  visual?: boolean;
  visualQ?: string;
  specNotes?: string;
}
export interface ProdApprovalResponse {
  oid: string;
  prodId?: string;
  name?: string;
  department?: string;
  date?: string;
  notes?: string;
  createdBy?: string;
  createdDate?: string;
  lastUpdatedBy?: string;
  lastUpdateDate?: string;
}
export interface EditProductFormValues {
  // Section I – Product Info (restricted fields for Approved)
  productSAPNumber?: string;
  formerName?: string;
  isCOQDisplay?: string;
  preCode?: string;
  prodRMId?: string;
  notes?: string;

  // Section I – Product Info (Pending-only fields)
  name?: string;
  description?: string;
  registrationTypeId?: string;
  productTypeId?: string;
  pslID?: string;
  shelfLifeYears?: number;
  issueDate?: string;
  tradeMarkOwner?: string;
  casNo?: string;
  supercedes?: string;
  supercedeId?: string;

  // Section II – Formula Notes
  formulaNotes?: string;

  // Section III – Manufacturing Description
  mDescription?: string;

  // Spec Notes
  specNotes?: string;

  // Section IV – Typical Properties
  typicalProperties?: string;

  // Section V – Packaging
  packagingNotes?: string;
  prodPackage?: string;
  pkgType?: string;
  prodImageURL?: string;

  // Section VI – Handling & Storage
  storagePrecaution?: string;

  // Section VII – Transport Regulations
  usaDot?: string;
  hazardClass?: string;
  hazardSubstance?: string;
  htsNumber?: string;
  codePageNum?: string;
  reportableQty?: string;
  classNum?: string;
  pkgingGroup?: string;
  motorFrightNum?: string;
  // DOT
  dot_Label?: string;
  dot_PlaCard?: string;
  dot_IDNum?: string;
  dot_DOTNotes?: string;
  dot_ShippingName?: string;
  // IMO
  imo_Label?: string;
  imo_PlaCard?: string;
  imo_IDNum?: string;
  imo_Notes?: string;
  imo_ShippingName?: string;
  // International / EEC
  reqACountries?: string;
  eecCountries?: string;

  // Section IX – History
  history?: string;

  // Supplier Notes
  supplierNotes?: string;
}
export function buildProductUpdate(product: ProductDetail, values: EditProductFormValues, currentUser: string) {
  const prodId = product.oid;
  const isPending = product.status === ProductStatus.Pending;
  const isEditable = isPending || product.status === ProductStatus.Approved;
  return {
          oid: prodId,

          // ── Always editable (Approved + Pending) ── core fields
          productSAPNumber: values.productSAPNumber,
          formerName: values.formerName,
          isCOQDisplay: values.isCOQDisplay,
          preCode: values.preCode,
          prodRMId: values.prodRMId,

          // ── Section I detail — Pending-only (locked for Approved) ──
          ...(isPending && {
            name: values.name,
            description: values.description,
            registrationTypeId: values.registrationTypeId,
            productTypeId: values.productTypeId,
            pslID: values.pslID,
            shelfLifeYears: values.shelfLifeYears,
            issueDate: values.issueDate,
            tradeMarkOwner: values.tradeMarkOwner,
            casNo: values.casNo,
            supercedes: values.supercedes,
          }),

          // ── Other sections — editable for both Pending & Approved ──
          ...(isEditable && {
            // Section II – Formula Notes
            formulaNotes: values.formulaNotes,
            // Section III – Mfg Description
            mDescription: values.mDescription,
            // Spec Notes
            specNotes: values.specNotes,
            // Section IV – Typical Properties
            typicalProperties: values.typicalProperties,
            // Section V – Packaging
            packagingNotes: values.packagingNotes,
            prodPackage: values.prodPackage,
            pkgType: values.pkgType,
            prodImageURL: values.prodImageURL,
            // Section VI – Storage
            storagePrecaution: values.storagePrecaution,
            // Section VII – Transport Regulations
            usaDot: values.usaDot,
            hazardClass: values.hazardClass,
            hazardSubstance: values.hazardSubstance,
            htsNumber: values.htsNumber,
            codePageNum: values.codePageNum,
            reportableQty: values.reportableQty,
            classNum: values.classNum,
            pkgingGroup: values.pkgingGroup,
            motorFrightNum: values.motorFrightNum,
            dot_Label: values.dot_Label,
            dot_PlaCard: values.dot_PlaCard,
            dot_IDNum: values.dot_IDNum,
            dot_DOTNotes: values.dot_DOTNotes,
            dot_ShippingName: values.dot_ShippingName,
            imo_Label: values.imo_Label,
            imo_PlaCard: values.imo_PlaCard,
            imo_IDNum: values.imo_IDNum,
            imo_Notes: values.imo_Notes,
            imo_ShippingName: values.imo_ShippingName,
            reqACountries: values.reqACountries,
            eecCountries: values.eecCountries,
            // Supplier Notes
            supplierNotes: values.supplierNotes,
          }),

          lastUpdatedBy: currentUser,
        };
}
